import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; user?: User }>;
  impersonate: (userId: number) => Promise<{ success: boolean; user?: User }>;
  stopImpersonation: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isImpersonating: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const ADMIN_TOKEN_KEY = 'admin_token';
const IMPERSONATOR_ID_KEY = 'impersonator_id';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
      if (token) {
        const response = await authService.checkAuth();
        if (response?.user) {
          setUser(response.user);
        } else {
          localStorage.removeItem('token');
          if (adminToken) localStorage.removeItem(ADMIN_TOKEN_KEY);
          localStorage.removeItem(IMPERSONATOR_ID_KEY);
        }
      } else if (adminToken) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_ID_KEY);
      }
    } catch (error) {
      console.log('Not authenticated');
      localStorage.removeItem('token');
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(IMPERSONATOR_ID_KEY);
    } finally {
      setLoading(false);
      setIsImpersonating(!!localStorage.getItem(ADMIN_TOKEN_KEY));
    }
  };

  const login = async (username: string, password: string): Promise<{ success: boolean; user?: User }> => {
    try {
      const response = await authService.login(username, password);
      if (response.user && response.token) {
        setUser(response.user);
        localStorage.setItem('token', response.token);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATOR_ID_KEY);
        setIsImpersonating(false);
        return { success: true, user: response.user };
      }
      return { success: false };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false };
    }
  };

  const impersonate = async (userId: number): Promise<{ success: boolean; user?: User }> => {
    try {
      const response = await authService.impersonate(userId);
      if (response.user && response.token) {
        const currentToken = localStorage.getItem('token');
        if (currentToken && !localStorage.getItem(ADMIN_TOKEN_KEY)) {
          localStorage.setItem(ADMIN_TOKEN_KEY, currentToken);
        }
        localStorage.setItem('token', response.token);
        if (response.impersonator_id) {
          localStorage.setItem(IMPERSONATOR_ID_KEY, String(response.impersonator_id));
        }
        setUser(response.user);
        setIsImpersonating(true);
        return { success: true, user: response.user };
      }
      return { success: false };
    } catch (error) {
      console.error('Impersonation failed:', error);
      return { success: false };
    }
  };

  const stopImpersonation = async () => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminToken) return;
    localStorage.setItem('token', adminToken);
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATOR_ID_KEY);
    setIsImpersonating(false);
    await checkAuthStatus();
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      localStorage.removeItem(IMPERSONATOR_ID_KEY);
      setIsImpersonating(false);
    }
  };

  const refreshUser = async () => {
    await checkAuthStatus();
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    impersonate,
    stopImpersonation,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
